import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"
import { uniqBy } from "lodash"

import { useLazyGetAdminDevicesQuery } from "store/api/adminApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useRunDeviceCommandMutation } from "store/api/performanceTestApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import {
  showDamageDeviceModalForVendor,
  showDeviceHistoryLogModal,
  showMissingModalForVendor,
  showReturnToWarehouseModal,
  showTransferDeviceModal,
  showUndamageDeviceModalForVendor,
  showWarrantyModal,
} from "store/modalSlise"
import { setDeviceHistoryLogID, setSelectedDeviceIDs, setSelectedDevices } from "store/tempDataSlise"
import {
  deviceWorkStatusOptions,
  hvacOptions,
  vendorDevicePhysicalStatusOptions,
  vendorDeviceRedFlagOptions,
  vendorDeviceRedFlagTypeOptions,
} from "constants/SelectFieldOptions"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as IconLog } from "assets/svg/icon-log.svg"
import { ReactComponent as IconPower } from "assets/svg/icon-power.svg"
import { ReactComponent as IconStatsReport } from "assets/svg/icon-stats-report.svg"

import { convertVendorDevicesToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import DeviceRebootModal from "modals/DeviceRebootModal/DeviceRebootModal"
import { StatsReportModal } from "modals/StatsReportModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { FormattedPhoneNumber } from "components/table/columnComponents/PhoneNumber"
import { StatusOfPhysical } from "components/table/columnComponents/StatusOfPhysical"
import { StatusOfWork } from "components/table/columnComponents/StatusOfWork"
import TableWrapper from "components/table/TableWrapper"
import { WidgetInstaledDevices } from "components/widgets/WidgetInstaledDevices"
import { WidgetMobileAppUsage } from "components/widgets/WidgetMobileAppUsage"
import { WidgetPhysicalStatus } from "components/widgets/WidgetPhysicalStatus"

import styles from "./index.module.scss"

const initialTableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "",
    name: "checkbox",
    type: "checkbox",
    size: "mini",
    cellComponent: "checkbox",
  },
  {
    title: "S/N",
    type: "sort-search",
    size: "middle",
    name: "sn",
  },
  {
    title: "Work Status",
    type: "select",
    size: "small",
    name: "work_status",
    options: deviceWorkStatusOptions,
    cellComponent: StatusOfWork,
  },
  {
    title: "Physical status",
    type: "select",
    size: "small",
    name: "phisical_status",
    options: vendorDevicePhysicalStatusOptions,
    cellComponent: StatusOfPhysical,
  },
  {
    title: "Contractor",
    type: "sort-search",
    size: "middle",
    name: "contractor_name",
  },
  {
    title: "Activation date",
    type: "sort-date-range",
    size: "middle",
    name: "activation_date",
  },
  {
    title: "Red Flag",
    size: "small",
    type: "select",
    name: "red_flag",
    options: vendorDeviceRedFlagOptions,
  },
  {
    title: "Red Flag Type",
    type: "select",
    size: "middle",
    name: "red_flag_type",
    options: vendorDeviceRedFlagTypeOptions,
  },
  {
    title: "Customer",
    type: "sort-search",
    size: "middle",
    name: "homeowner",
  },
  {
    title: "Country",
    type: "sort-search",
    size: "small",
    name: "country",
  },
  {
    title: "Address",
    type: "sort-search",
    name: "address",
  },
  {
    title: "Email",
    type: "sort-search",
    size: "middle",
    name: "email",
  },
  {
    title: "Phone",
    type: "sort-search",
    size: "middle",
    name: "phone",
    cellComponent: FormattedPhoneNumber,
  },
  {
    title: "Installation type",
    type: "select",
    size: "small",
    name: "installation_type",
    options: hvacOptions,
  },
  {
    title: "Device Location",
    type: "sort-search",
    name: "device_location",
  },
  {
    title: "System age",
    type: "sort-search",
    size: "middle",
    name: "system_age",
  },
]

export const Devices = () => {
  const dispatch = useAppDispatch()
  const [triggerGetAdminDevices, { isFetching, data }] = useLazyGetAdminDevicesQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const [triggerRunReboot] = useRunDeviceCommandMutation()
  const selectedDeviceIDs = useAppSelector(state => state.tempData.selectedDeviceIDs)
  const selectedDevices = useAppSelector(state => state.tempData.selectedDevices)
  const [isFirstRender, setFirstRender] = useState(true)
  const [isHeaderChecked, setHeaderChecked] = useState(false)
  const [showRebootModalDevice, setShowRebootModalDevice] = useState<ApiTypes.Model.Device | undefined>()
  const [selectedDeviceForStatsReport, setSelectedDeviceForStatsReport] = useState<ApiTypes.Model.Device | undefined>()
  const devices = data?.data
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(initialTableHeaderCells, columnsData?.data?.config)
        : initialTableHeaderCells,
    [columnsData],
  )

  const showDamageModal = () => {
    dispatch(showDamageDeviceModalForVendor(true))
  }

  const showUndamageModal = () => {
    dispatch(showUndamageDeviceModalForVendor(true))
  }

  const showMissingModal = () => {
    dispatch(showMissingModalForVendor(true))
  }

  const onTransferDeviceClick = () => {
    dispatch(showTransferDeviceModal(true))
  }

  const onReturnToWarehouseClick = () => {
    dispatch(showReturnToWarehouseModal(true))
  }

  const onReturnFromWarrantyClick = () => {
    dispatch(showWarrantyModal(true))
  }

  const onShowHistoryLogModal = (id: number) => {
    dispatch(setDeviceHistoryLogID(id))
    dispatch(showDeviceHistoryLogModal(true))
  }

  const onClearSelectedDevices = () => {
    dispatch(setSelectedDeviceIDs([]))
    dispatch(setSelectedDevices([]))
    setHeaderChecked(false)
  }

  const onDeviceSelect = (id: number) => {
    if (selectedDeviceIDs?.some(item => item === id)) {
      const ids = selectedDeviceIDs?.filter(item => item !== id)
      dispatch(setSelectedDeviceIDs(ids))
    } else {
      dispatch(setSelectedDeviceIDs([...selectedDeviceIDs, ...[id]]))
    }
  }

  const toggleAllDevices = () => {
    if (isHeaderChecked) {
      setHeaderChecked(false)
      dispatch(setSelectedDeviceIDs([]))
    } else {
      const ids = devices?.map(item => item.id)
      dispatch(setSelectedDeviceIDs(ids || []))
      setHeaderChecked(true)
    }
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetAdminDevices(params)

      const errorMessage = getErrorMessage(res.error)
      if (errorMessage) {
        toast.error(errorMessage)
      }
    } catch (e) {
      const error = e as Error
      toast.error(error.message)
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    sendRequest(urlQueryParams)
    triggerGetTableColumns({ list_name: "admin_devices_list" })
  }, [urlQueryParams, isFirstRender])

  // get devices accordingly to deviceIDs
  useEffect(() => {
    let result: ApiTypes.Model.VendorDevice[] = []

    if (!selectedDevices?.length && devices) {
      result = devices?.filter(item => selectedDeviceIDs?.some(id => id === item?.id))
    } else if (selectedDevices?.length && devices) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      result = uniqBy([...selectedDevices, ...devices], "id")?.filter((item: ApiTypes.Model.VendorDevice) =>
        selectedDeviceIDs?.some(id => id === item?.id),
      )
    }

    dispatch(setSelectedDevices(result))
  }, [selectedDeviceIDs])

  const checkButtonsAvailability = (value: string[]) => {
    if (!selectedDevices?.length) return false
    return selectedDevices?.every(item => value.some(status => status === item.phisical_status))

    /*
      
      -- AVAILABILITY RULS --
      
      mark as damaged - stocked, warranty
      mark as undamaged - damaged
      transfer - stocked, transferring
      mark as missing - stocked, inventory
      return to warehouse - transferring
      return from warranty - warranty

    */
  }

  const handleSendLogs = async (id: number) => {
    const device = devices?.find(item => item.id === id)
    await triggerRunReboot({ sn: device?.sn || "", command: "push_logs" })
  }

  const handleShowStatsReportModal = (id: number) => {
    const device = devices?.find(item => item.id === id)

    setSelectedDeviceForStatsReport({
      id: device?.id || 0,
      sn: device?.sn,
      email: device?.email,
      zip: { code: device?.zip_code },
      homeowner_name: device?.homeowner,
      system_type: device?.system_type,
      accessories: device?.accessories,
      time_zone_text: device?.time_zone_text,
    } as unknown as ApiTypes.Model.Device)
  }

  const handleCancelStatsReportModal = () => {
    setSelectedDeviceForStatsReport(undefined)
  }

  const handleShowRebootModal = (id: number) => {
    const device = devices?.find(item => item.id === id)

    setShowRebootModalDevice(device as unknown as ApiTypes.Model.Device)
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    const isOffline = rowData.rowSettings.item.work_status === "offline"

    return {
      actionButtons: [
        {
          icon: <IconPower />,
          tooltipPosition: "top" as const,
          tooltip: (
            <>
              Reboot device
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
            </>
          ),
          disabled: isOffline,
          handler: handleShowRebootModal,
        },
        {
          icon: <IconLog />,
          tooltipPosition: "top-start" as const,
          tooltip: (
            <>
              Send Logs to DB
              {isOffline && (
                <>
                  <br />
                  (device is offline)
                </>
              )}
            </>
          ),
          disabled: isOffline,
          handler: handleSendLogs,
        },
        {
          icon: <IconStatsReport />,
          tooltipPosition: "top-start" as const,
          tooltip: "Monitoring report",
          handler: handleShowStatsReportModal,
        },
      ],
    }
  }

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetPhysicalStatus />
        <WidgetInstaledDevices />
        <WidgetMobileAppUsage />
      </div>
      <ContentWrapper title="Devices">
        <nav className={styles.tableNavWrapper}>
          <div className={classNames(styles.actionButtonsWrapper)}>
            <button
              onClick={onTransferDeviceClick}
              disabled={!checkButtonsAvailability(["stocked", "transferring"])}
              className={classNames(styles.actionButton, styles.transfer)}
            >
              Transfer
            </button>
            <button
              onClick={showMissingModal}
              disabled={!checkButtonsAvailability(["stocked", "inventory"])}
              className={classNames(styles.actionButton, styles.missing)}
            >
              Mark as missing
            </button>
            <button
              onClick={showDamageModal}
              disabled={!checkButtonsAvailability(["stocked", "warranty"])}
              className={classNames(styles.actionButton, styles.damaged)}
            >
              Mark as Damaged
            </button>
            <button
              onClick={onReturnToWarehouseClick}
              disabled={!checkButtonsAvailability(["transferring"])}
              className={classNames(styles.actionButton, styles.warehouse)}
            >
              Return to Warehouse
            </button>
            <button
              onClick={onReturnFromWarrantyClick}
              disabled={!checkButtonsAvailability(["warranty"])}
              className={classNames(styles.actionButton, styles.warranty)}
            >
              Return from Warranty
            </button>
            <button
              onClick={showUndamageModal}
              disabled={!checkButtonsAvailability(["damaged"])}
              className={classNames(styles.actionButton, styles.damaged)}
            >
              Mark as Undamaged
            </button>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <EditColumnsButton tableName="admin_devices_list" tableHeaderCells={tableHeaderCells} />
            {/* <ButtonNew className={styles.tableNavItem}>Export</ButtonNew> */}
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!!columnsData}
          isLoading={isFetching}
          currentItemIDs={selectedDeviceIDs}
          onCheckboxChange={onDeviceSelect}
          data={convertVendorDevicesToTableRows(devices, tableHeaderCells, onShowHistoryLogModal)}
          onHeaderCheckboxChange={toggleAllDevices}
          isHeaderChecked={isHeaderChecked}
          clearAdditionalTags={() => onClearSelectedDevices()}
          additionalTags={
            selectedDevices?.length
              ? [
                  {
                    label: "Device S/N",
                    values: selectedDevices?.map(item => item.sn),
                  },
                ]
              : []
          }
          getRowActions={getRowActions}
        />
        <Pagination meta={data?.meta} />
        {showRebootModalDevice && (
          <DeviceRebootModal device={showRebootModalDevice} onClose={() => setShowRebootModalDevice(undefined)} />
        )}
        {selectedDeviceForStatsReport && (
          <StatsReportModal device={selectedDeviceForStatsReport} onCancel={handleCancelStatsReportModal} isVendor />
        )}
      </ContentWrapper>
    </>
  )
}
